<template>
  <div class="dc">
    <demand-graph
      :weekday-slots="weekdaySlots"
      :weekend-slots="weekendSlots"
    />

    <!-- Set your blocks & max couples -->
    <div class="dc-blocks-card">
      <div class="dc-title-row">
        <div class="dc-title">
          Set your blocks &amp; max couples
        </div>
        <button
          type="button"
          class="dc-howto"
          :class="{ active: showPacing }"
          title="How pacing works"
          @click="showPacing = !showPacing"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 16 16"
            fill="none"
          >
            <circle
              cx="8"
              cy="8"
              r="7"
              :stroke="showPacing ? colors.orange : colors.textSec"
              stroke-width="1.4"
            />
            <circle
              cx="8"
              cy="4.7"
              r="1"
              :fill="showPacing ? colors.orange : colors.textSec"
            />
            <path
              d="M8 7v4.5"
              :stroke="showPacing ? colors.orange : colors.textSec"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
          How it works
        </button>

        <!-- Pacing popover -->
        <template v-if="showPacing">
          <div
            class="dc-pop-scrim"
            @click="showPacing = false"
          />
          <div class="dc-pop">
            <div class="dc-pop-arrow" />
            <div class="dc-pop-head">
              <div class="dc-pop-head-left">
                <div class="dc-pop-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      :stroke="colors.orange"
                      stroke-width="1.4"
                    />
                    <circle
                      cx="8"
                      cy="4.7"
                      r="1"
                      :fill="colors.orange"
                    />
                    <path
                      d="M8 7v4.5"
                      :stroke="colors.orange"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>
                <div class="dc-pop-title">
                  How pacing works
                </div>
              </div>
              <button
                type="button"
                class="dc-pop-close"
                aria-label="Close"
                @click="showPacing = false"
              >✕</button>
            </div>
            <div class="dc-pop-divider" />
            <div class="dc-pop-body">
              <div
                v-for="(row, i) in pacingRows"
                :key="i"
                class="dc-pop-row"
                :class="{ first: i === 0 }"
              >
                <div class="dc-pop-num">
                  {{ i + 1 }}
                </div>
                <div>
                  <div class="dc-pop-row-title">
                    {{ row.title }}
                  </div>
                  <div class="dc-pop-row-body">
                    <component
                      :is="seg.b ? 'strong' : 'span'"
                      v-for="(seg, j) in row.body"
                      :key="j"
                    >{{ seg.t }}</component>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="dc-blocks-sub">
        Drag to resize, hold to move. Blocks can't overlap. We stop seating {{ duration }} min before a block ends,
        so arrival times are automatic.
      </div>

      <div class="dc-legend">
        <span class="dc-legend-item">
          <span class="dc-legend-chip ok">✓</span> a {{ duration }}-min date fits here
        </span>
        <span class="dc-legend-item">
          <span class="dc-legend-chip bad">✕</span> won't fit, can't be booked
        </span>
      </div>

      <div
        v-for="day in daysData"
        :key="day.key"
        class="dc-day"
      >
        <div class="dc-day-head">
          <input
            :id="`dc-day-${day.key}`"
            type="checkbox"
            class="dc-day-check"
            :checked="days[day.key].enabled"
            @change="toggleDay(day.key)"
          >
          <label
            :for="`dc-day-${day.key}`"
            class="dc-day-name"
            :class="{ off: !days[day.key].enabled }"
          >{{ day.label }}</label>
          <span
            v-if="!days[day.key].enabled"
            class="dc-day-empty"
          >No dates</span>
        </div>
        <day-block-editor
          v-if="days[day.key].enabled"
          :value="days[day.key]"
          :day="day"
          :duration="duration"
          @input="updateDay(day.key, $event)"
        />
      </div>
    </div>

    <!-- 1-on-1 reservation settings -->
    <div class="dc-res-card">
      <div class="dc-res-title">
        1-on-1 reservation settings
      </div>

      <div class="dc-res-rows">
        <div class="dc-res-row">
          <div class="dc-res-info">
            <div class="dc-res-label">
              <span>Average date duration</span>
              <span
                v-if="!managedByBreeze"
                class="dc-match-pill"
              >⚠ Must match your booking system</span>
            </div>
            <div class="dc-res-hint">
              <component
                :is="seg.b ? 'strong' : 'span'"
                v-for="(seg, j) in durHint"
                :key="j"
              >{{ seg.t }}</component>
            </div>
          </div>
          <select
            class="dc-dur-sel"
            aria-label="Average date duration in minutes"
            :value="duration"
            @change="onDurationChange($event)"
          >
            <option
              v-for="v in durationOptions"
              :key="v"
              :value="v"
            >{{ v }} min</option>
          </select>
        </div>

        <div class="dc-res-row divided">
          <div class="dc-res-info">
            <div class="dc-res-label">
              <span>Time between new arrivals</span>
              <span
                v-if="!managedByBreeze"
                class="dc-match-pill"
              >⚠ Must match your booking system</span>
            </div>
            <div class="dc-res-hint">
              {{ gapHint }}
            </div>
          </div>
          <div class="dc-gap-btns">
            <button
              v-for="v in [0, 15]"
              :key="v"
              type="button"
              class="dc-gap-btn"
              :class="{ active: arrivalGap === v }"
              @click="arrivalGap = v"
            >{{ v }} min</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="dc-footer">
      <button
        type="button"
        class="dc-discard"
        :disabled="saving"
        @click="discard"
      >Discard</button>
      <button
        type="button"
        class="dc-save"
        :disabled="saving"
        @click="requestSave"
      >{{ saving ? 'Saving…' : 'Save changes' }}</button>
    </div>

    <!-- Match-your-booking-system modal -->
    <div
      v-if="showDurInfo"
      class="dc-modal-scrim"
      @click="showDurInfo = false"
    >
      <div
        class="dc-modal"
        @click.stop
      >
        <div class="dc-modal-head">
          <div class="dc-modal-head-left">
            <div class="dc-modal-icon">
              ⚠
            </div>
            <div class="dc-modal-title">
              Match this to your booking system
            </div>
          </div>
          <button
            type="button"
            class="dc-modal-close"
            aria-label="Close"
            @click="showDurInfo = false"
          >✕</button>
        </div>
        <div class="dc-modal-divider" />
        <div class="dc-modal-body">
          <div class="dc-modal-text">
            We use this duration to work out when to send couples, but
            <strong>we can't read or change the date length in your booking system.</strong>
          </div>
          <div class="dc-modal-box">
            Open your booking system and set its date / reservation duration to the <strong>same value</strong>
            you pick here.
          </div>
          <div class="dc-modal-text muted">
            If they don't match, couples may arrive before a table is free, or dates may run past the end of your block.
          </div>
          <div class="dc-modal-actions">
            <button
              type="button"
              class="dc-modal-ok"
              @click="showDurInfo = false"
            >Got it</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Save-confirmation modal -->
    <div
      v-if="showSaveConfirm"
      class="dc-modal-scrim"
      @click="showSaveConfirm = false"
    >
      <div
        class="dc-modal"
        @click.stop
      >
        <div class="dc-modal-head">
          <div class="dc-modal-head-left">
            <div class="dc-modal-icon">
              ℹ
            </div>
            <div class="dc-modal-title">
              Save changes?
            </div>
          </div>
          <button
            type="button"
            class="dc-modal-close"
            aria-label="Close"
            @click="showSaveConfirm = false"
          >✕</button>
        </div>
        <div class="dc-modal-divider" />
        <div class="dc-modal-body">
          <div class="dc-modal-text">
            Matches that were allocated the location before this change will keep their existing reservation. This update will only apply to matches allocated from now on.
          </div>
          <div class="dc-modal-actions">
            <button
              type="button"
              class="dc-modal-cancel"
              @click="showSaveConfirm = false"
            >Cancel</button>
            <button
              type="button"
              class="dc-modal-ok"
              @click="confirmSave"
            >Save changes</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { requests } from '@/_helpers/requests'
import { colors } from '@/design-system'
import DemandGraph from './DemandGraph.vue'
import DayBlockEditor from './DayBlockEditor.vue'
import {
  DAYS_DATA, daysFromSlots, daysToTimeSlots, addMin, isBreezeManaged,
} from './capacity-helpers'

const slotsKey = slots => JSON.stringify(slots.map(({ id, ...rest }) => rest))

export default {
  name: 'DateCapacity',
  components: { DemandGraph, DayBlockEditor },
  // locationId is passed by Preferences; the demand fetch is partner-scoped (derived
  // from the JWT), but we keep it for the forthcoming location-scoped settings save.
  props: {
    locationId: {
      type: String,
      default: null,
    },
    // The venue's configured time slots (from the deals' timeSlots). Each becomes a bar.
    timeSlots: {
      type: Array,
      default: () => [],
    },
    // Average date duration and arrival gap, persisted on the deal.
    durationMinutes: {
      type: Number,
      default: 90,
    },
    gapMinutes: {
      type: Number,
      default: 0,
    },
    // The deal whose duration/gap the save updates. Null = don't touch deal capacity.
    dealId: {
      type: String,
      default: null,
    },
    reservationMethod: {
      type: String,
      default: null,
    },
  },
  data() {
    const days = daysFromSlots(this.timeSlots, this.durationMinutes)
    return {
      colors,
      daysData: DAYS_DATA,
      days,
      duration: this.durationMinutes,
      arrivalGap: this.gapMinutes,
      // The last persisted duration/gap. Changes are diffed against this (not the props) so
      // that after a successful save the new values become the baseline and an unchanged
      // re-save sends reservationSettings: null.
      savedDuration: this.durationMinutes,
      savedGap: this.gapMinutes,
      // The last persisted slot set. discard() rebuilds from this (not the prop, which save()
      // never updates), so discarding after an all-cleared save reverts to the saved empty
      // state rather than resurrecting the pre-save slots.
      savedTimeSlots: daysToTimeSlots(days, this.durationMinutes),
      // Signature of the last persisted slot set, diffed against the current state so an
      // unchanged save never hits the backend (a reconcile would needlessly rewrite rows).
      savedSlotsKey: slotsKey(daysToTimeSlots(days, this.durationMinutes)),
      showPacing: false,
      showDurInfo: false,
      showSaveConfirm: false,
      saving: false,
      weekdaySlots: [],
      weekendSlots: [],
      durationOptions: [60, 75, 90, 105, 120, 135, 150, 165, 180],
    }
  },
  computed: {
    managedByBreeze() {
      return isBreezeManaged(this.reservationMethod)
    },
    // A block must be longer than the date duration to leave a seating window: openUntil =
    // barEnd - duration must be > openFrom, else the backend rejects it (openUntil <= openFrom
    // reads as an overnight slot). So length <= duration is invalid. The editor flags these;
    // we block the save until they're fixed.
    hasTooShortBlock() {
      const durSlots = Math.ceil(this.duration / 15)
      return this.daysData.some(d => {
        const day = this.days[d.key]
        return day && day.enabled && day.blocks.some(b => (b.to - b.from) <= durSlots)
      })
    },
    // True when the working state differs from what's persisted. The slot signature folds in
    // duration (it shifts openUntil), so a duration-only change still reads as dirty.
    isDirty() {
      return this.duration !== this.savedDuration
        || this.arrivalGap !== this.savedGap
        || slotsKey(daysToTimeSlots(this.days, this.duration)) !== this.savedSlotsKey
    },
    lastSeating() {
      return addMin('22:00', -this.duration)
    },
    pacingRows() {
      // body is a list of segments; { b: true } renders bold (was inline <strong>).
      return [
        {
          title: 'A block is the whole stretch you set aside for Breeze.',
          body: [
            { t: 'A ' },
            { t: '17:00–22:00', b: true },
            { t: ' block with a ' },
            { t: `${this.duration} min`, b: true },
            { t: ' date means we stop seating new couples at ' },
            { t: this.lastSeating, b: true },
            { t: ', so the last table is free by closing. You never set arrival times; we subtract the date length for you.' },
          ],
        },
        {
          title: 'Max couples is your simultaneous limit.',
          body: [
            { t: 'Within a block, up to your ' },
            { t: 'max couples', b: true },
            { t: ' are seated at once. Tables turn over all evening, so you host many more than that across the night.' },
          ],
        },
        {
          title: 'Separate blocks, separate limits.',
          body: [
            { t: "An afternoon block at max 3 won't eat into an evening block at max 6. Each is counted on its own." },
          ],
        },
      ]
    },
    durHint() {
      return this.managedByBreeze
        ? [
          { t: 'We place every reservation for you, so just set your average date length. ' },
          { t: 'Pick whatever works for your venue.', b: true },
        ]
        : [
          { t: 'Defaults to 90 min. ' },
          { t: "Set this to match your reservation system's date duration", b: true },
          { t: ". We can't sync it for you." },
        ]
    },
    gapHint() {
      return this.managedByBreeze
        ? 'We handle spacing between the couples we send, so set this only if you want a gap between new arrivals.'
        : 'Use this only when your reservation system requires gaps between bookings.'
    },
  },
  watch: {
    timeSlots(slots) {
      this.days = daysFromSlots(slots, this.durationMinutes)
      this.savedTimeSlots = daysToTimeSlots(this.days, this.durationMinutes)
      this.savedSlotsKey = slotsKey(this.savedTimeSlots)
    },
    durationMinutes(val) {
      this.duration = val
      this.savedDuration = val
      this.days = daysFromSlots(this.timeSlots, val)
      this.savedTimeSlots = daysToTimeSlots(this.days, val)
      this.savedSlotsKey = slotsKey(this.savedTimeSlots)
    },
    gapMinutes(val) {
      this.arrivalGap = val
      this.savedGap = val
    },
  },
  created() {
    this.fetchDemand()
  },
  methods: {
    fetchDemand() {
      return requests.get('partner-portal/date-arrival-times')
        .then(response => {
          const data = response.data || {}
          this.weekdaySlots = data.weekdaySlots || []
          this.weekendSlots = data.weekendSlots || []
        })
        .catch(() => {
          this.weekdaySlots = []
          this.weekendSlots = []
        })
    },
    updateDay(key, data) {
      this.days = { ...this.days, [key]: data }
    },
    toggleDay(key) {
      this.days = { ...this.days, [key]: { ...this.days[key], enabled: !this.days[key].enabled } }
    },
    onDurationChange(e) {
      this.duration = parseInt(e.target.value, 10)
      if (!this.managedByBreeze) this.showDurInfo = true
    },
    // Body for PUT .../locations/:id/time-slots: the complete desired slot set plus the
    // (mandatory) dealId. It's a reconcile, so we always send every slot we want to exist.
    // reservationSettings is null unless duration/gap actually changed, so an unchanged save
    // leaves the deal's capacity untouched.
    toRequestBody() {
      const settingsChanged = this.duration !== this.savedDuration || this.arrivalGap !== this.savedGap
      return {
        timeSlots: daysToTimeSlots(this.days, this.duration),
        dealId: this.dealId,
        reservationSettings: settingsChanged
          ? { durationMinutes: this.duration, gapMinutes: this.arrivalGap }
          : null,
      }
    },
    discard() {
      this.duration = this.savedDuration
      this.arrivalGap = this.savedGap
      // Revert to the last saved slot set.
      this.days = daysFromSlots(this.savedTimeSlots, this.savedDuration)
    },
    // Gate the save behind a confirmation dialog. The guard cases (too-short block, nothing
    // changed) are delegated to save(), which owns their toasts, so the dialog only pops for a
    // real, valid change and the toast copy lives in one place.
    requestSave() {
      if (this.saving) return
      if (this.hasTooShortBlock || !this.isDirty) {
        this.save()
        return
      }
      this.showSaveConfirm = true
    },
    confirmSave() {
      this.showSaveConfirm = false
      this.save()
    },
    save() {
      if (this.saving) return undefined
      if (this.hasTooShortBlock) {
        this.$notify.blocked(`Some time blocks are too short for your ${this.duration}-minute date. Each block must be longer than the date, so lengthen them or lower the date duration before saving.`)
        return undefined
      }
      // Nothing changed: skip the backend (a reconcile would needlessly rewrite the rows)
      if (!this.isDirty) {
        this.$notify.success('Your date capacity has been saved.')
        return undefined
      }
      this.saving = true
      return requests.put(`partner-portal/locations/${this.locationId}/time-slots`, this.toRequestBody())
        .then(response => {
          // The response mirrors the request body: { timeSlots, dealId, reservationSettings }.
          const data = response.data || {}
          // Adopt the persisted settings the server echoes back (null = deal left untouched)...
          const rs = data.reservationSettings
          if (rs && rs.durationMinutes != null) this.duration = rs.durationMinutes
          if (rs && rs.gapMinutes != null) this.arrivalGap = rs.gapMinutes
          // ...then re-read the reconciled slots with that duration so the bars (openUntil +
          // duration) match. Ids aren't stable (a weekday change is delete + create).
          this.days = daysFromSlots(data.timeSlots || [], this.duration)
          // Whatever is now in state is persisted: an unchanged re-save sends null settings
          // and (via savedSlotsKey) skips the request entirely. The saved snapshot is the new
          // baseline a subsequent discard reverts to.
          this.savedDuration = this.duration
          this.savedGap = this.arrivalGap
          this.savedTimeSlots = daysToTimeSlots(this.days, this.duration)
          this.savedSlotsKey = slotsKey(this.savedTimeSlots)
          this.$notify.success('Your date capacity has been saved.')
        })
        .catch(() => {
          this.$notify.error('We couldn\'t save your changes. Please check your blocks and try again.', { title: 'Save failed' })
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/tokens';

.dc {
  color: $c-text;
}

.dc-blocks-card {
  background: $c-white;
  border: 1px solid $c-border;
  border-radius: 16px;
  padding: 24px 28px;
  margin-bottom: 14px;
  box-shadow: 0 4px 22px rgba(41, 6, 27, 0.06);
}

.dc-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 3px;
  position: relative;
}

.dc-title {
  font-weight: 700;
  font-size: 19px;
  white-space: nowrap;
  letter-spacing: -0.02em;
}

.dc-howto {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid $c-border;
  background: $c-white;
  color: $c-text-sec;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  line-height: 1;

  &.active {
    border-color: $c-orange;
    background: $c-orange-light;
    color: $c-orange;
  }
}

.dc-pop-scrim {
  position: fixed;
  inset: 0;
  z-index: 14;
}

.dc-pop {
  position: absolute;
  top: 34px;
  left: 0;
  z-index: 15;
  width: 384px;
  max-width: 88vw;
  background: $c-white;
  border-radius: 14px;
  border: 1px solid $c-border;
  box-shadow: 0 18px 50px rgba(41, 6, 27, 0.22);
  overflow: hidden;
}

.dc-pop-arrow {
  position: absolute;
  top: -7px;
  left: 20px;
  width: 12px;
  height: 12px;
  background: $c-white;
  border-top: 1px solid $c-border;
  border-left: 1px solid $c-border;
  transform: rotate(45deg);
}

.dc-pop-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
}

.dc-pop-head-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dc-pop-icon {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: $c-orange-light;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dc-pop-title {
  font-weight: 700;
  font-size: 15.5px;
  color: $c-text;
}

.dc-pop-close {
  background: none;
  border: none;
  color: $c-text-ter;
  font-size: 17px;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.dc-pop-divider {
  border-top: 1px solid $c-border-light;
}

.dc-pop-body {
  padding: 16px 20px 18px;
}

.dc-pop-row {
  display: flex;
  gap: 10px;
  padding-top: 12px;
  margin-top: 12px;
  border-top: 1px solid $c-border-light;

  &.first {
    padding-top: 0;
    margin-top: 0;
    border-top: none;
  }
}

.dc-pop-num {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: $c-orange-light;
  color: $c-orange;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.dc-pop-row-title {
  font-size: 12.5px;
  font-weight: 700;
  color: $c-text;
  margin-bottom: 2px;
}

.dc-pop-row-body {
  font-size: 12px;
  line-height: 1.55;
  color: $c-text-sec;

  ::v-deep strong { color: $c-text; }
}

.dc-blocks-sub {
  font-size: 13px;
  color: $c-text-sec;
  margin-bottom: 12px;
  line-height: 1.6;
}

.dc-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  font-size: 12.5px;
  color: $c-text-sec;
}

.dc-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dc-legend-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;

  &.ok {
    background: $c-success-light;
    color: #1f7a45;
    border: 1px solid rgba($c-success, 0.25);
  }

  &.bad {
    background: $c-danger-light;
    color: $c-danger;
    border: 1px solid rgba($c-danger, 0.19);
  }
}

.dc-day {
  background: $c-bg;
  border: 1px solid $c-border-light;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 20px;
}

.dc-day-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.dc-day-check {
  width: 17px;
  height: 17px;
  accent-color: $c-wine;
  cursor: pointer;
}

.dc-day-name {
  font-weight: 700;
  font-size: 15.5px;
  color: $c-text;
  width: 108px;

  &.off { color: $c-text-ter; }
}

.dc-day-empty {
  font-size: 12px;
  color: $c-text-ter;
  font-style: italic;
}

.dc-res-card {
  background: $c-white;
  border: 1px solid $c-border;
  border-radius: 12px;
  padding: 14px 16px;
}

.dc-res-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
}

.dc-res-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dc-res-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;

  &.divided {
    align-items: center;
    border-top: 1px solid $c-border-light;
    padding-top: 10px;
  }
}

.dc-res-info {
  flex: 1;
  min-width: 0;
}

.dc-res-label {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  font-weight: 500;
}

.dc-match-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 100px;
  border: 1px solid rgba($c-orange, 0.33);
  background: rgba($c-orange, 0.08);
  color: $c-orange;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.dc-res-hint {
  font-size: 11px;
  color: $c-text-sec;
  margin-top: 2px;

  ::v-deep strong { color: $c-text; }
}

.dc-dur-sel {
  padding: 7px 30px 7px 12px;
  border: 1px solid $c-border;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  color: $c-wine;
  background-color: $c-white;
  cursor: pointer;
  flex-shrink: 0;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path d='M3 5l3 3 3-3' fill='none' stroke='%2329061B' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 11px center;
  background-size: 11px;
}

.dc-gap-btns {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.dc-gap-btn {
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid $c-border;
  background: $c-white;
  color: $c-text;
  font-weight: 400;

  &.active {
    border: 2px solid $c-wine;
    background: $c-wine-light;
    color: $c-wine;
    font-weight: 600;
  }
}

.dc-footer {
  position: sticky;
  bottom: 0;
  background: $c-white;
  border-top: 1px solid $c-border;
  padding: 10px 0;
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  z-index: 10;
}

.dc-discard {
  padding: 8px 16px;
  border-radius: 7px;
  border: 1px solid $c-border;
  background: $c-white;
  color: $c-text-sec;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
}

.dc-save {
  padding: 8px 18px;
  border-radius: 7px;
  border: none;
  background: $c-orange;
  color: $c-wine;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.dc-modal-scrim {
  position: fixed;
  inset: 0;
  background: rgba(41, 6, 27, 0.35);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dc-modal {
  background: $c-white;
  border-radius: 14px;
  max-width: 440px;
  width: 90%;
  max-height: 80vh;
  overflow: auto;
  box-shadow: 0 18px 50px rgba(41, 6, 27, 0.22);
}

.dc-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
}

.dc-modal-head-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dc-modal-icon {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: $c-orange-light;
  color: $c-orange;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
}

.dc-modal-title {
  font-weight: 700;
  font-size: 15.5px;
  color: $c-text;
}

.dc-modal-close {
  background: none;
  border: none;
  font-size: 17px;
  color: $c-text-ter;
  cursor: pointer;
  line-height: 1;
}

.dc-modal-divider {
  border-top: 1px solid $c-border-light;
}

.dc-modal-body {
  padding: 16px 20px 20px;
}

.dc-modal-text {
  font-size: 13.5px;
  line-height: 1.6;
  color: $c-text-sec;
  margin-bottom: 14px;

  strong { color: $c-text; }

  &.muted {
    font-size: 12.5px;
    margin-bottom: 0;
  }
}

.dc-modal-box {
  background: $c-bg;
  border: 1px solid $c-border-light;
  border-radius: 10px;
  padding: 13px 15px;
  font-size: 13px;
  line-height: 1.6;
  color: $c-text;
  margin-bottom: 14px;
}

.dc-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.dc-modal-cancel {
  padding: 9px 20px;
  border-radius: 9px;
  border: 1px solid $c-border-light;
  background: transparent;
  color: $c-text;
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

.dc-modal-ok {
  padding: 9px 20px;
  border-radius: 9px;
  border: none;
  background: $c-orange;
  color: #ffffff;
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}
</style>
